import React from 'react';
import { Link } from 'react-router-dom';
import { Truck, ArrowLeft, Clock, Mail, Download, CheckCircle } from 'lucide-react';

const DeliveryPage = () => {
  return (
    <div data-testid="delivery-page" className="min-h-screen bg-[#FAFAF8]">
      {/* Header */}
      <section className="bg-white py-12 border-b border-slate-100">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl">
          <Link to="/" className="inline-flex items-center text-[#4CAF50] hover:text-[#43A047] mb-6">
            <ArrowLeft className="w-5 h-5 mr-2" />
            Grįžti į pradžią
          </Link>
          <div className="flex items-center space-x-4">
            <div className="w-14 h-14 bg-[#E3F2FD] rounded-2xl flex items-center justify-center">
              <Truck className="w-8 h-8 text-[#5B9BD5]" />
            </div>
            <div>
              <h1 className="text-3xl md:text-4xl font-['Poppins'] font-bold text-[#2D3748]">
                Pristatymo sąlygos
              </h1>
              <p className="text-[#718096]">Paskutinis atnaujinimas: 2025 m. sausio mėn.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-12">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-4xl">
          <div className="bg-white rounded-3xl p-8 md:p-12 border border-slate-100 prose prose-slate max-w-none">
            
            {/* Important Notice */}
            <div className="bg-[#E8F5E9] rounded-2xl p-6 flex items-start space-x-4 mb-8">
              <Download className="w-6 h-6 text-[#4CAF50] flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-lg font-semibold text-[#2D3748] mt-0 mb-2">Skaitmeniniai produktai</h3>
                <p className="text-[#718096] text-sm mb-0">
                  Visi mūsų parduotuvėje siūlomi produktai yra skaitmeniniai (PDF vadovai, video kursai, 
                  audio failai). Jie pristatomi automatiškai el. paštu iš karto po sėkmingo apmokėjimo.
                </p>
              </div>
            </div>

            <h2 className="text-2xl font-['Poppins'] font-semibold text-[#2D3748] mt-0">1. Pristatymo būdas</h2>
            <p className="text-[#718096] leading-relaxed">
              Visi skaitmeniniai produktai pristatomi elektroniniu būdu:
            </p>
            <ul className="text-[#718096] space-y-2">
              <li><strong>El. paštu:</strong> Atsisiuntimo nuoroda siunčiama į jūsų nurodytą el. pašto adresą</li>
              <li><strong>Paskyros skiltyje:</strong> Jei turite paskyrą, produktus rasite savo profilyje</li>
            </ul>

            <h2 className="text-2xl font-['Poppins'] font-semibold text-[#2D3748]">2. Pristatymo laikas</h2>
            
            <div className="grid md:grid-cols-2 gap-4 my-6">
              <div className="bg-[#E8F5E9] rounded-2xl p-5">
                <div className="flex items-center space-x-2 mb-2">
                  <Clock className="w-5 h-5 text-[#4CAF50]" />
                  <h3 className="font-semibold text-[#2D3748] m-0">Automatinis pristatymas</h3>
                </div>
                <p className="text-[#718096] text-sm m-0">
                  Iš karto po sėkmingo mokėjimo patvirtinimo (paprastai per kelias minutes)
                </p>
              </div>
              <div className="bg-[#FFF3E0] rounded-2xl p-5">
                <div className="flex items-center space-x-2 mb-2">
                  <Mail className="w-5 h-5 text-[#FF9F43]" />
                  <h3 className="font-semibold text-[#2D3748] m-0">El. pašto pristatymas</h3>
                </div>
                <p className="text-[#718096] text-sm m-0">
                  Iki 15 minučių po mokėjimo (priklausomai nuo el. pašto serverio)
                </p>
              </div>
            </div>

            <h2 className="text-2xl font-['Poppins'] font-semibold text-[#2D3748]">3. Atsisiuntimo instrukcijos</h2>
            <ol className="text-[#718096] space-y-2">
              <li><strong>1 žingsnis:</strong> Patikrinkite savo el. paštą (ir "Spam" aplanką)</li>
              <li><strong>2 žingsnis:</strong> Atidarykite laišką su tema "Jūsų užsakymas TavoEmocijos"</li>
              <li><strong>3 žingsnis:</strong> Spauskite atsisiuntimo nuorodą</li>
              <li><strong>4 žingsnis:</strong> Išsaugokite failą savo įrenginyje</li>
            </ol>

            <h2 className="text-2xl font-['Poppins'] font-semibold text-[#2D3748]">4. Atsisiuntimo limitai</h2>
            <ul className="text-[#718096] space-y-2">
              <li>Kiekvienas produktas gali būti atsisiųstas <strong>iki 5 kartų</strong></li>
              <li>Atsisiuntimo nuoroda galioja <strong>30 dienų</strong> nuo pirkimo</li>
              <li>Jei reikia papildomo atsisiuntimo, susisiekite su mumis</li>
            </ul>

            <h2 className="text-2xl font-['Poppins'] font-semibold text-[#2D3748]">5. Techniniai reikalavimai</h2>
            <p className="text-[#718096] leading-relaxed">
              Priklausomai nuo produkto tipo, jums gali prireikti:
            </p>
            <ul className="text-[#718096] space-y-2">
              <li><strong>PDF failams:</strong> Adobe Reader arba kita PDF skaitymo programa</li>
              <li><strong>Video failams:</strong> VLC, Windows Media Player ar kitas video grotuvas</li>
              <li><strong>Audio failams:</strong> Bet koks audio grotuvas (MP3 formatas)</li>
            </ul>

            <h2 className="text-2xl font-['Poppins'] font-semibold text-[#2D3748]">6. Problemos su pristatymu?</h2>
            
            <div className="bg-[#F3E5F5] rounded-2xl p-6 my-6">
              <h3 className="text-lg font-semibold text-[#2D3748] mt-0 mb-3">Jei negavote produkto:</h3>
              <ul className="text-[#718096] text-sm space-y-2 mb-0">
                <li><CheckCircle className="w-4 h-4 text-[#9B59B6] inline mr-2" />Patikrinkite "Spam" arba "Šiukšlės" aplanką</li>
                <li><CheckCircle className="w-4 h-4 text-[#9B59B6] inline mr-2" />Įsitikinkite, kad nurodėte teisingą el. pašto adresą</li>
                <li><CheckCircle className="w-4 h-4 text-[#9B59B6] inline mr-2" />Palaukite iki 15 minučių po mokėjimo</li>
                <li><CheckCircle className="w-4 h-4 text-[#9B59B6] inline mr-2" />Susisiekite su mumis, jei problema išlieka</li>
              </ul>
            </div>

            <h2 className="text-2xl font-['Poppins'] font-semibold text-[#2D3748]">7. Mokymų ir konsultacijų pristatymas</h2>
            <p className="text-[#718096] leading-relaxed">
              Individualios koučingo sesijos ir grupiniai mokymai vyksta:
            </p>
            <ul className="text-[#718096] space-y-2">
              <li><strong>Nuotoliniu būdu:</strong> Per Zoom arba kitą vaizdo konferencijų platformą</li>
              <li><strong>Gyvai:</strong> Sutartoje vietoje (Vilniuje arba kliento patalpose)</li>
            </ul>
            <p className="text-[#718096] leading-relaxed">
              Tikslus laikas ir vieta derinami individualiai po užsakymo patvirtinimo.
            </p>

            <h2 className="text-2xl font-['Poppins'] font-semibold text-[#2D3748]">8. Kontaktai</h2>
            <p className="text-[#718096] leading-relaxed">
              Pristatymo klausimais kreipkitės:
            </p>
            <ul className="text-[#718096] space-y-2">
              <li><strong>El. paštas:</strong> info@tavoemocijos.lt</li>
              <li><strong>Telefonas:</strong> +370 678 38321</li>
              <li><strong>Atsakymo laikas:</strong> iki 24 valandų</li>
            </ul>

          </div>
        </div>
      </section>
    </div>
  );
};

export default DeliveryPage;
